import React from 'react';
import { Button, Icon } from '@kontentino/ui';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faPause } from '@fortawesome/pro-solid-svg-icons/faPause';
import { useTranslation } from 'react-i18next';
import TimeUtils from 'app/utils/time';
import { useAudioVideoMixPreview } from '../hooks/useAudioVideoMixPreview';
import { isAudioMixPreviewSupported } from '../utils/isAudioMixPreviewSupported';

interface InstagramAudioMixPreviewProps {
  videoSrc: string | null;
  audioSrc: string | null;
  audioVolume: number;
  videoVolume: number;
  audioDurationMs: number | null;
  videoDurationMs: number | null;
}

const InstagramAudioMixPreview = ({
  videoSrc,
  audioSrc,
  audioVolume,
  videoVolume,
  audioDurationMs,
  videoDurationMs,
}: InstagramAudioMixPreviewProps) => {
  const { t } = useTranslation();
  const { videoRef, audioRef, isPlaying, isBuffering, toggle } =
    useAudioVideoMixPreview({
      videoSrc,
      audioSrc,
      audioVolume,
      videoVolume,
    });

  if (!audioSrc || !isAudioMixPreviewSupported()) {
    return null;
  }

  const lengthMismatchHint =
    audioDurationMs !== null &&
    videoDurationMs !== null &&
    audioDurationMs > videoDurationMs
      ? t('instagramAudio.lengthMismatch', {
          trackDuration:
            TimeUtils.calculateDurationFromMilliseconds(audioDurationMs),
          videoDuration:
            TimeUtils.calculateDurationFromMilliseconds(videoDurationMs),
        })
      : null;

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-2"
      data-name="instagram-audio_mix-preview"
    >
      <div className="tw-flex tw-items-center tw-gap-3">
        {videoSrc && (
          <video
            ref={videoRef}
            src={videoSrc}
            className="tw-h-20 tw-w-20 tw-shrink-0 tw-rounded-md tw-bg-grayscale-5 tw-object-cover"
            playsInline
            preload="metadata"
          />
        )}

        <audio ref={audioRef} src={audioSrc} preload="metadata" />

        <Button
          variant="secondary"
          size="small"
          isLoading={isBuffering}
          iconBefore={<Icon icon={isPlaying ? faPause : faPlay} size="sm" />}
          onClick={toggle}
          data-cy="instagram-audio_mix-preview-button"
        >
          {videoSrc
            ? t('instagramAudio.previewMix')
            : t('instagramAudio.previewTrack')}
        </Button>
      </div>

      {lengthMismatchHint && (
        <span className="tw-text-xs tw-text-grayscale-140">
          {lengthMismatchHint}
        </span>
      )}
    </div>
  );
};

export default InstagramAudioMixPreview;
